import gsap from 'gsap';
import { Container, Graphics } from 'pixi.js';
import { gameConfig } from '../config/gameConfig.js';

/**
 * Transparent clipped shimmer sweep inside bounds (no border/background).
 *
 * @param {import('pixi.js').Container} host
 * @param {{ x: number, y: number, w: number, h: number, r?: number }} bounds
 * @param {import('../animation/AnimationController.js').AnimationController | null} animationController
 * @param {{ loop?: boolean, shimmerRepeats?: number, shimmerColor?: number, shimmerAlpha?: number }} [opts]
 * @returns {{ overlayRoot: import('pixi.js').Container, stop: () => void }}
 */
export function attachLuxuryGoldFrameFx(host, bounds, animationController, opts = {}) {
  if (!host || !bounds) {
    return { overlayRoot: null, stop: () => {} };
  }

  const { x, y, w, h, r = 12 } = bounds;
  const loop = opts.loop !== false;
  const t = gameConfig.theme ?? {};
  const hi = opts.shimmerColor ?? t.modalBorderGoldHi ?? t.goldBright ?? 0xf5e6a8;
  const shimmerFillAlpha = opts.shimmerAlpha ?? 0.38;

  /** @type {import('gsap').Tween[]} */
  const tweens = [];
  const track = (tw) => {
    if (!tw) return tw;
    tweens.push(tw);
    animationController?.trackTween?.(tw);
    return tw;
  };

  const clipMask = new Graphics();
  clipMask.roundRect(x, y, w, h, r).fill(0xffffff);
  clipMask.eventMode = 'none';

  const overlayRoot = new Container();
  overlayRoot.eventMode = 'none';
  overlayRoot.mask = clipMask;

  const shimmer = new Graphics();
  shimmer.alpha = 0;
  overlayRoot.addChild(shimmer);
  const shimmerW = Math.max(20, w * 0.34);
  const shimmerSpan = w + shimmerW * 2;
  const drawShimmer = (offset) => {
    shimmer.clear();
    const sx = x + offset;
    shimmer.moveTo(sx, y);
    shimmer.lineTo(sx + shimmerW * 0.35, y);
    shimmer.lineTo(sx + shimmerW, y + h);
    shimmer.lineTo(sx + shimmerW * 0.65, y + h);
    shimmer.closePath();
    shimmer.fill({ color: hi, alpha: shimmerFillAlpha });
  };
  drawShimmer(-shimmerW);

  const shimmerState = { t: 0 };
  const shimmerDuration = 2.4;
  const shimmerDelay = loop ? 1.1 : 0.55;
  const shimmerRepeats = loop ? -1 : Math.max(0, (opts.shimmerRepeats ?? 2) - 1);

  const shimmerTween = gsap.to(shimmerState, {
    t: 1,
    duration: shimmerDuration,
    ease: 'power2.inOut',
    onStart: () => {
      shimmer.alpha = 0.82;
    },
    onUpdate: () => {
      drawShimmer(-shimmerW + shimmerState.t * shimmerSpan);
    },
    onComplete: () => {
      shimmer.alpha = 0;
    },
  });

  const shimmerTl = gsap.timeline({ repeat: shimmerRepeats, repeatDelay: shimmerDelay });
  shimmerTl.add(shimmerTween);
  track(shimmerTl);

  host.addChild(clipMask);
  host.addChild(overlayRoot);

  const stop = () => {
    for (const tw of tweens) {
      tw.kill();
    }
    tweens.length = 0;
    overlayRoot.mask = null;
    if (clipMask.parent) clipMask.parent.removeChild(clipMask);
    if (overlayRoot.parent) overlayRoot.parent.removeChild(overlayRoot);
    clipMask.destroy();
    overlayRoot.destroy({ children: true });
  };

  return { overlayRoot, stop };
}
